import React from 'react';
import { RightOutlined } from '@ant-design/icons';

import { useUIColors } from '../../theme';
import { cx, type Styleable } from '../../variants';

export interface ItemProps extends Styleable {
    /** Primary line, 14px/600, truncated on overflow. */
    title: React.ReactNode;
    /** Secondary line, 12px muted. Carries the reason when `disabled`. */
    caption?: React.ReactNode;
    /** 30–34px leading tile — an avatar, a monospace code square, an icon. */
    leading?: React.ReactNode;
    /** Trailing metadata (count, timestamp, status pill). */
    trailing?: React.ReactNode;
    selected?: boolean;
    disabled?: boolean;
    /** Renders a trailing chevron and makes the whole row activatable. */
    onClick?: () => void;
    /** Shows the chevron without requiring `onClick` (e.g. an anchor wrapper). */
    showChevron?: boolean;
    /**
     * EAA-1817: accessible name for an interactive row. Needed whenever the
     * visible content is FRAGMENTED — a coverage rail row reads out as "lms LMS
     * 1 6/12" from its own nodes, which is four facts and no sentence. Without
     * this the only way to name the row was to abandon `Item` and hand-write a
     * `<button aria-label=…>`, which is what the caller that needed it did.
     *
     * Omitted (every existing caller), the row keeps deriving its name from its
     * content exactly as before — no attribute is emitted.
     */
    ariaLabel?: string;
    /**
     * EAA-1817: how `selected` is drawn.
     *
     * `'ring'` (default, every existing caller) — a 1px inset ring all round.
     * `'edge'` — a 3px accent bar on the leading edge only, which is what the
     * handoff draws for a selected row in a vertical rail (`box-shadow: inset
     * 3px 0 0 #007fa3` plus a fainter tint). The two read differently: a ring
     * says "this box is picked", an edge says "this row of a list is the one
     * open beside it", and a rail whose rows are all the same width has no box
     * to outline in the first place.
     *
     * A variant rather than a `className` override because the ring is an inline
     * style here — a caller's class cannot beat it without `!important`, which
     * is precisely the workaround the kit's styling contract exists to avoid.
     */
    selectedVariant?: 'ring' | 'edge';
}

/**
 * EAA-1804: the sheet's generic list-row primitive.
 *
 * One row shape shared by app lists, tenant lists and notification rows, so
 * those three stop each re-deriving padding, gap and selected-tint from
 * scratch and drifting apart. Ant Design's `List.Item` is close but imposes
 * its own metadata layout (`List.Item.Meta`) and horizontal rules; the sheet
 * specifies a flat `flex` row with a leading tile, which is simpler to render
 * directly than to talk `List` out of.
 *
 * A disabled row keeps its title readable and puts the reason in `caption` —
 * the sheet is explicit that an unavailable option must say why rather than
 * just going grey.
 */
export function Item({
    title,
    caption,
    leading,
    trailing,
    selected = false,
    disabled = false,
    onClick,
    showChevron,
    ariaLabel,
    selectedVariant = 'ring',
    className,
    style,
}: ItemProps): React.ReactElement {
    const colors = useUIColors();
    const interactive = Boolean(onClick) && !disabled;
    const chevron = showChevron ?? interactive;

    return (
        <div
            className={cx(className)}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-label={ariaLabel}
            aria-disabled={disabled || undefined}
            onClick={interactive ? onClick : undefined}
            onKeyDown={
                interactive
                    ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onClick?.();
                          }
                      }
                    : undefined
            }
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '11px 14px',
                borderRadius: 8,
                background: selected ? colors.sidebarMenuSelectedBg : 'transparent',
                // Selection is an inset shadow, never a border — a border would
                // shift every unselected sibling by a pixel as focus moves.
                boxShadow: selected
                    ? selectedVariant === 'edge'
                        ? `inset 3px 0 0 0 ${colors.cta}`
                        : `inset 0 0 0 1px ${colors.cta}`
                    : undefined,
                cursor: interactive ? 'pointer' : 'default',
                transition: 'background 120ms ease-out, box-shadow 120ms ease-out',

                ...style,
            }}
        >
            {leading && <span style={{ flex: 'none', display: 'inline-flex' }}>{leading}</span>}

            <span style={{ flex: 1, minWidth: 0 }}>
                <span
                    style={{
                        display: 'block',
                        fontSize: 14,
                        fontWeight: 600,
                        color: disabled ? colors.textMuted : colors.text,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {title}
                </span>
                {caption && (
                    <span style={{ display: 'block', fontSize: 12, color: colors.textMuted }}>
                        {caption}
                    </span>
                )}
            </span>

            {trailing && <span style={{ flex: 'none' }}>{trailing}</span>}
            {chevron && (
                <RightOutlined style={{ flex: 'none', fontSize: 12, color: colors.textMuted }} />
            )}
        </div>
    );
}

export default Item;
